/**
 * Navigation Selector
 * Determines which navigation to show based on user role and selected customer
 */

import { RoleCategory, ROLE_HIERARCHY, getHighestRoleLevel, RoleName } from '../roles';
import { NavGroup, NavItem } from './types';
import { ADMIN_GENERAL_NAVIGATION } from './adminGeneralNavigation';
import { ADMIN_CUSTOMER_NAVIGATION } from './adminCustomerNavigation';
import { OWNER_NAVIGATION } from './ownerNavigation';
import { getStaffNavigation } from './staffNavigation';
import { Customer } from '../../types/customer';
import { getCustomerNavigation } from './customerNavigation';
import { logger } from '../../services/logger';

export * from './types';

/**
 * Get role level for a category
 */
function getRoleLevelForCategory(category: RoleCategory | null): number {
  switch (category) {
    case RoleCategory.ADMIN:
      return 90; // Admin level
    case RoleCategory.MANAGER:
      return 70; // Síndico level
    case RoleCategory.STAFF:
      return 60; // Porteiro level
    case RoleCategory.RESIDENT:
      return 40; // Morador level
    case RoleCategory.GUEST:
      return 10; // Visitante level
    default:
      return 0;
  }
}

/**
 * Filter navigation groups based on user's role level
 */
function filterNavigationByRoleLevel(navigation: NavGroup[], roleLevel: number, userRoles?: string[]): NavGroup[] {
  return navigation
    .filter(group => {
      // If group has minRoleLevel, check if user meets it
      if (group.minRoleLevel !== undefined && roleLevel < group.minRoleLevel) {
        return false;
      }
      // If group has maxRoleLevel, check if user doesn't exceed it
      if (group.maxRoleLevel !== undefined && roleLevel > group.maxRoleLevel) {
        return false;
      }
      return true;
    })
    .map(group => {
      // Filter items within group based on minRoleLevel, maxRoleLevel, and excludeRoles
      if (group.items) {
        const filteredItems = group.items.filter(item => {
          if (item.minRoleLevel !== undefined && roleLevel < item.minRoleLevel) {
            return false;
          }
          if (item.maxRoleLevel !== undefined && roleLevel > item.maxRoleLevel) {
            return false;
          }
          if (item.excludeRoles && userRoles?.some(r => item.excludeRoles!.includes(r))) {
            return false;
          }
          return true;
        });

        // If no items left after filtering, exclude the group
        if (filteredItems.length === 0) {
          return null;
        }

        return { ...group, items: filteredItems };
      }
      return group;
    })
    .filter((group): group is NavGroup => group !== null);
}

/**
 * Get navigation based on user's role category and selected customer
 *
 * @param roleCategory - User's role category (ADMIN, MANAGER, STAFF, RESIDENT, GUEST)
 * @param selectedCustomer - Currently selected customer (null for admins in general view)
 * @param isUserBound - Whether user is bound to a specific customer
 * @param userRoles - Optional: User's actual roles for more precise level calculation
 * @returns Appropriate navigation configuration
 */
export function getNavigationForUser(
  roleCategory: RoleCategory | null,
  selectedCustomer: Customer | null,
  isUserBound: boolean,
  userRoles?: string[]
): NavGroup[] {
  // Check if user is administrador (special handling - gets full navigation like manager but with restrictions)
  const isAdministrador = userRoles?.includes('administrador') ?? false;

  logger.debug('navigation.config', 'Getting navigation for user', {
    roleCategory,
    selectedCustomer: selectedCustomer?.name || 'none',
    isUserBound,
    userRoles,
    isAdministrador
  });

  // Use actual role level if userRoles provided, otherwise use category default
  const roleLevel = userRoles ? getHighestRoleLevel(userRoles) : getRoleLevelForCategory(roleCategory);

  // Admin logic: Show different nav based on customer selection
  if (roleCategory === RoleCategory.ADMIN) {
    if (selectedCustomer) {
      logger.info('navigation.config', 'Admin with customer selected, using customer-specific navigation', {
        customerType: selectedCustomer.type
      });
      // Admins get filtered navigation based on their role level (90)
      // This excludes resident-only items (maxRoleLevel < 90)
      return filterNavigationByRoleLevel(getCustomerNavigation(selectedCustomer), roleLevel, userRoles);
    } else {
      logger.info('navigation.config', 'Admin without customer, using general admin navigation');
      return ADMIN_GENERAL_NAVIGATION;
    }
  }

  // Manager (Síndico) logic: Use customer-specific navigation if customer is selected
  if (roleCategory === RoleCategory.MANAGER) {
    if (selectedCustomer) {
      logger.info('navigation.config', 'Manager with customer selected, using customer-specific navigation', {
        customerType: selectedCustomer.type
      });
      // Managers get navigation filtered by their role level
      return filterNavigationByRoleLevel(getCustomerNavigation(selectedCustomer), roleLevel, userRoles);
    }
    logger.info('navigation.config', 'Manager without customer, using staff navigation');
    return getStaffNavigation(selectedCustomer);
  }

  // Staff (Porteiro, Zelador, etc.) logic: Use staff navigation (simplified)
  // Exception: Administrador gets customer-specific navigation like managers (but with level-based restrictions)
  if (roleCategory === RoleCategory.STAFF) {
    // Administrador gets full customer navigation (filtered by their role level 65)
    if (isAdministrador && selectedCustomer) {
      logger.info('navigation.config', 'Administrador user with customer, using customer-specific navigation', {
        customerType: selectedCustomer.type,
        roleLevel
      });
      return filterNavigationByRoleLevel(getCustomerNavigation(selectedCustomer), roleLevel, userRoles);
    }

    logger.info('navigation.config', 'Staff user, using simplified staff navigation', {
      hasCustomer: !!selectedCustomer,
      isAdministrador
    });
    // Regular staff (porteiro, zelador) always gets the simplified STAFF_NAVIGATION
    return getStaffNavigation(selectedCustomer);
  }

  // Resident/Owner logic: Use customer-specific navigation if customer is selected
  if (roleCategory === RoleCategory.RESIDENT) {
    if (selectedCustomer) {
      logger.info('navigation.config', 'Resident with customer selected, using customer-specific navigation', {
        customerType: selectedCustomer.type
      });
      return filterNavigationByRoleLevel(getCustomerNavigation(selectedCustomer), roleLevel, userRoles);
    }
    logger.info('navigation.config', 'Resident without customer, using owner navigation');
    return OWNER_NAVIGATION;
  }

  // Guest logic: No navigation
  if (roleCategory === RoleCategory.GUEST) {
    logger.info('navigation.config', 'Guest user, returning empty navigation');
    return [];
  }

  // Fallback for unknown roles
  logger.warn('navigation.config', 'Unknown role, returning default owner navigation');
  return OWNER_NAVIGATION;
}
